<template>
  <footer class="lib__form__footer">
    <nav class="lib__btns-nav-list">
      <a :class="getState('reload', loading, true)" :disabled="!!loading" @click.prevent="tryReset"
        class="lib__btn lib__btn--link" data-step="reload" href="#" v-if="hasReset">
        <i class="fas fa-sync"></i>
        <span>{{ $t('common.resetRequest') }}</span>
      </a>

      <a :class="getState('prev', loading, true)" :disabled="!!loading" @click.prevent="tryPrevious"
        class="lib__btn lib__btn--dark lib__btn--outline lib__btn--rounded" href="#" type="button" v-if="hasPrevious">
        <slot name="previous">
          <i class="fas fa-chevron-left"></i>
          <span>{{ $t('common.previousButton') }}</span>
        </slot>
      </a>
      <slot></slot>
      <a :class="getState('next', loading, canContinue)" :disabled="!canContinue || !!loading" @click.prevent="tryNext"
        class="lib__btn lib__btn--primary lib__btn--rounded" data-cy="btn-next-step" href="#" type="button"
        v-if="hasNext">
        <slot name="next">
          <span>{{ nextButtonLabel }}</span>
          <i class="fas fa-chevron-right"></i>
        </slot>
      </a>
      <a :class="getState('print', loading, true)" :disabled="!!loading" @click.prevent="print"
        class="lib__btn lib__btn--primary lib__btn--rounded" data-cy="btn-print-step" href="#" v-if="hasPrint">
        <slot name="print">
          <i class="fas fa-print"></i>
          <span>{{ $t('common.print') }}</span>
        </slot>
      </a>
    </nav>
    <nav class="lib__text-center" style="margin-top:30px;">
      <a class="lib__btn lib__btn--link lib__btn--secondary" :class="getState('save', loading, true)"
        :disabled="!!loading" @click.prevent="save" href="#" v-if="hasSave">
        <i class="fas fa-save"></i>
        <span>{{ $t('common.saveAndContinueLater') }}</span>
      </a>
    </nav>
    <div v-if="showFaq" class="row center-xs">
      <i18n :path="faqLabel" tag="span" for="edit">
        <button v-if="showChatButton" class="lib__chat_trigger lib__btn lib__btn--primary lib__btn--small"
          @click="openChat"><i class="fas fa-comments"></i></button>
        <a @click="trackClick" v-if="faqLink && linkLabel" target="_blank" :href="faqLink"
          class="lib__no-margin lib__link__mobile--underline" v-html="linkLabel"></a>
      </i18n>
    </div>
  </footer>
</template>

<script lang="ts">
import Vue from 'vue'
import Component from 'vue-class-component'
import { Prop, Emit } from 'vue-property-decorator'
import { Mutation, State } from 'vuex-class'
import { chatService } from '@/services'

@Component({ name: 'step-actions' })
export default class StepActions extends Vue {
  //#region [Property]
  @Prop({ type: Boolean, required: false, default: true }) public readonly hasPrevious!: boolean
  @Prop({ type: Boolean, required: false, default: true }) public readonly hasNext!: boolean
  @Prop({ type: Boolean, required: false, default: false }) public readonly hasPrint!: boolean
  @Prop({ type: Boolean, required: false, default: false }) public readonly hasReset!: boolean
  @Prop({ type: Boolean, required: false, default: false }) public readonly hasSave!: boolean
  @Prop({ type: Boolean, required: false, default: false }) public readonly hasSend!: boolean
  @Prop({ type: String, required: false, default: null }) public readonly loading!: string
  @Prop({ type: Boolean, required: false, default: true }) public readonly canContinue!: boolean
  @Prop({ type: String, required: false, default: null }) public readonly nextLabel!: string
  @Prop({ type: Boolean, required: false, default: false }) public readonly showFaq!: boolean
  @Prop({ type: String, required: false, default: null }) public readonly faqLabel!: string
  @Prop({ type: Boolean, required: false, default: false }) public readonly showChatButton!: boolean
  @Prop({ type: String, required: false, default: null }) public readonly faqLink!: string
  @Prop({ type: String, required: false, default: null }) public readonly linkLabel!: string
  //#endregion

  //#region [Data]
  @State('requestId', { namespace: 'eMobility' })
  public requestId!: string
  //#endregion

  //#region [Computed]
  private get nextButtonLabel(): string {
    if (this.hasSend) {
      return this.$t('common.sendLabel').toString()
    }
    return this.nextLabel ? this.nextLabel : this.$t('common.nextButton').toString()
  }
  //#endregion

  //#region [Mutation]
  @Mutation('SET_ISBACK')
  private setIsBack!: (data: boolean) => void
  //#endregion

  //#region [Method]
  public print(): void {
    window.print()
  }

  public getState(key: string, loading: string, canContinue: boolean) {
    if (!loading) {
      return canContinue || key !== 'next' ? null : 'lib__btn--disabled'
    }

    return key === loading ? 'lib__btn--loading' : 'lib__btn--disabled'
  }

  public tryNext(): void {
    if (this.canContinue && !this.loading) {
      this.next()
    }
  }

  public tryPrevious(): void {
    if (!this.loading) {
      this.previous()
    }
  }

  public tryReset(): void {
    if (!this.loading) {
      this.reset()
    }
  }

  public openChat() {
    chatService.openChat()
  }

  public trackClick(): void {
    const url = window.location.href
    let segment = ''
    if (url.includes('recharger-voiture') || url.includes('auto-opladen') || url.includes('fahrzeug-aufladen')) {
      segment = 'EMobility'
    }
    if (segment !== '') {
      this.$appInsights.trackEvent(`${segment}_linkClick`, {
        Segment: segment,
        WebRef: this.requestId,
        Link: this.faqLink
      })
    }
  }
  //#endregion

  //#region [Emit]
  @Emit('next')
  public next() {
    this.setIsBack(false)
    return true
  }

  @Emit('previous')
  public previous() {
    this.setIsBack(true)
    return true
  }

  @Emit('reset')
  public reset() {
    return true
  }

  @Emit('save')
  public save() {
    return true
  }
  //#endregion
}
</script>
